"use client";

import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useState, useRef, useEffect, useMemo } from "react";
import { ArrowRight } from "lucide-react";
import useSearch from "@core/helpers/client/hooks/useSearch";
import { useMenusContext } from "@core/helpers/client/contexts/MenusContext"
import BottomSheet from "@/components/ui/ButtomSheet";
import InputSearchBar from "@/components/SearchBarTop/InputSearchBar";
import ContentSearchProduct from "@/components/SearchBarTop/ContentSearchProduct";
import MobileSearchButton from "@/components/SearchBarTop/MobileSearchButton";
import { with_search_query } from "@core/helpers/search-url";
import useClickOutside from "@core/helpers/client/hooks/useClickOutside";
import useIsDesktop from "@core/helpers/client/hooks/useIsDesktop";


export default function SearchBarProduct({ widget, SearchTitle }) {
  const { search, set_search, search_on_change, state, set_search_history, delete_all_search_history } = useSearch({ limit: 4 });
  const { products, brands, categories, search_loading, search_history } = state;
  const { menus } = useMenusContext();
  const popular_search = useMemo(() => menus?.["popular_search"], [menus?.["popular_search"]])
  const pathname = usePathname()
  const router = useRouter()
  const searchParams = useSearchParams()

  // the term the current page is showing results for
  const active_search = searchParams.get("q") || searchParams.get("query") || "";

  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const mobileInputRef = useRef(null);

  // SSR و hydration هر دو نسخه را دارند؛ بعد از آن فقط نسخه‌ی همان viewport در DOM می‌ماند
  // (BottomSheet موبایل keepMounted است و محتوای جستجو را روی دسکتاپ هم می‌ساخت)
  const is_desktop = useIsDesktop();

  const closeSearchSurfaces = () => {
    setOpen(false);
    setMobileSearchOpen(false);
  };

  function handleSearch(e) {
    e.preventDefault();
    if (search?.length >= 2)
      router.push(with_search_query("/search", search))
    setOpen(false)
    setMobileSearchOpen(false)
  }

  function handleAutoSearch(e) {
    search_on_change(e);
  }


  function clear() {
    set_search("");
  }

  const hasResults = products?.length > 0 || brands?.length > 0 || categories?.length > 0;

  useClickOutside(ref, () => setOpen(false));

  useEffect(() => {
    setOpen(false)
  }, [pathname, active_search]);

  useEffect(() => {
    setMobileSearchOpen(false);
  }, [pathname, active_search]);

  // keep the searched term visible in the box; closing the dropdown falls back
  // to it instead of emptying the input
  useEffect(() => {
    if (!open) {
      // go through search_on_change so the suggestions match the term too
      search_on_change({ target: { value: active_search } })
    }
  }, [open, active_search]);


  return (
    <>
      {is_desktop !== true && <div className="w-full block lg:hidden">

        <MobileSearchButton
          SearchTitle={SearchTitle}
          onClick={() => {
            setMobileSearchOpen(true);
            // focus inside the click so mobile browsers open the keyboard
            mobileInputRef.current?.focus({ preventScroll: true });
          }}
        />

        <BottomSheet
          open={mobileSearchOpen}
          onClose={() => setMobileSearchOpen(false)}
          full={true}
          allowDragToClose={false}
          allowBackdropClose={true}
          keepMounted >

          <div className="sticky top-0 left-0 right-0 z-1 bg-jmt-white flex items-center gap-1 lg:gap-2 p-4">

            <button type="button" aria-label="بستن جستجو" onClick={() => setMobileSearchOpen(false)} className="p-1 lg:p-2" >
              <ArrowRight className="w-5 h-5 lg:h-6 lg:w-6" />
            </button>

            <InputSearchBar
              search={search}
              search_loading={search_loading}
              handleSearch={handleSearch}
              handleAutoSearch={handleAutoSearch}
              clear={clear}
              setOpen={() => { }}
              inputRef={mobileInputRef}
              onClose={() => setMobileSearchOpen(false)}
            />
          </div>

          <div className="px-4 my-2 h-full max-h-screen">
            <ContentSearchProduct
              widget={widget}
              search={search}
              search_loading={search_loading}
              products={products}
              brands={brands}
              categories={categories}
              popular_search={popular_search}
              search_history={search_history}
              hasResults={hasResults}
              set_search_history={set_search_history}
              delete_all_search_history={delete_all_search_history}
              onNavigate={closeSearchSurfaces}
            />
          </div>

        </BottomSheet>

      </div>}
      {is_desktop !== false && <div className="w-full hidden lg:block">

        {/* backdrowp */}
        {open && (
          <div className="w-full fixed inset-0 bg-black/40 backdrop-blur-xs z-30"></div>
        )}

        {/* search input */}
        <div ref={ref} className="relative z-30 w-full max-w-2xl">

          <InputSearchBar
            search={search}
            search_loading={search_loading}
            handleSearch={handleSearch}
            handleAutoSearch={handleAutoSearch}
            clear={clear}
            setOpen={setOpen}
          />

          {/* dropdown content */}
          <div className={`
             absolute left-0 w-full max-h-[80vh] overflow-auto bg-jmt-white shadow-medium rounded-md mt-2
             transition-all duration-150 origin-top
             ${open ? "opacity-100 scale-100" : "opacity-0 scale-95 pointer-events-none"}
        `}>
            <div className="p-4">
              <ContentSearchProduct
                widget={widget}
                search={search}
                search_loading={search_loading}
                products={products}
                brands={brands}
                categories={categories}
                popular_search={popular_search}
                search_history={search_history}
                hasResults={hasResults}
                set_search_history={set_search_history}
                delete_all_search_history={delete_all_search_history}
                onNavigate={closeSearchSurfaces}
              />
            </div>

          </div>

        </div>

      </div>}
    </>
  );
}





